import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { DollarSign, Plus, Building2, ShoppingCart, Receipt, CheckCircle2 } from "lucide-react";
import { API } from "@/apiConfig";
import { printServerRequest } from "@/lib/printServer";
import VendorDialog from "@/components/products/VendorDialog";
import ScanInvoiceButton from "@/components/ScanInvoiceButton";

export default function VendorPayoutDialog({ open, onOpenChange, onComplete }) {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [recordedPayout, setRecordedPayout] = useState(null); // holds payout after recording

  useEffect(() => {
    if (open) { fetchVendors(); resetForm(); }
  }, [open]);

  const fetchVendors = async () => {
    try { const r = await axios.get(`${API}/vendors`); setVendors(r.data); } catch {}
  };

  const resetForm = () => {
    setSelectedVendor(null);
    setAmount("");
    setNotes("");
    setRecordedPayout(null);
  };

  const printPayoutReceipt = async (payout) => {
    const stationId = localStorage.getItem("station_id") || "default";
    const employee = JSON.parse(localStorage.getItem("employee") || "{}");
    let storeSettings = {};
    try { const res = await axios.get(`${API}/settings/store`); storeSettings = res.data || {}; } catch {}
    let printerName = '';
    try { const hwRes = await axios.get(`${API}/settings/hardware?station_id=${stationId}`); printerName = hwRes.data?.receipt_printer_name || ''; } catch {}
    if (!printerName) { toast.error("No receipt printer configured"); return; }
    try {
      const response = await printServerRequest('post', '/api/print/vendor-payout', {
        printerName, storeSettings, vendorName: payout.vendor_name, amount: payout.amount,
        payoutId: payout.id || '', cashier: employee.name || payout.employee_name || 'N/A',
        stationId, notes: payout.notes || ''
      }, { timeoutMs: 3000 });
      if (!response.data?.success) throw new Error(response.data?.error || 'Print failed');
      toast.success("Payout receipt printed");
    } catch (error) {
      console.error('Vendor payout print failed:', error);
      toast.error("Failed to print receipt: " + error.message);
    }
  };

  const handleSubmitPayout = async () => {
    if (!selectedVendor) { toast.error("Please select a vendor"); return; }
    const payoutAmount = parseFloat(amount);
    if (isNaN(payoutAmount) || payoutAmount <= 0) { toast.error("Please enter a valid amount"); return; }
    const token = localStorage.getItem("token");
    const stationId = localStorage.getItem("station_id") || "default";
    setLoading(true);
    try {
      const res = await axios.post(`${API}/vendor-payouts`, {
        vendor_id: selectedVendor.id, vendor_name: selectedVendor.company_name,
        amount: payoutAmount, notes, station_id: stationId
      }, { headers: { Authorization: `Bearer ${token}` } });
      const payoutData = res.data || { vendor_name: selectedVendor.company_name, amount: payoutAmount, notes, id: '' };
      // Fire-and-forget print — don't block the UI
      printPayoutReceipt(payoutData).catch(() => {});
      toast.success(`Payout of $${payoutAmount.toFixed(2)} to ${selectedVendor.company_name} recorded`);
      setRecordedPayout({ ...payoutData, vendor_id: selectedVendor.id, vendor_name: selectedVendor.company_name, amount: payoutAmount });
      onComplete && onComplete();
    } catch { toast.error("Failed to record vendor payout"); }
    finally { setLoading(false); }
  };

  const handleVendorAdded = (newVendor) => {
    fetchVendors();
    setSelectedVendor(newVendor);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="vendor-payout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Vendor Payout
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {recordedPayout ? (
              /* Post-record state: show scan option */
              <div className="text-center space-y-4 py-4">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                <div>
                  <p className="font-bold text-zinc-900">${parseFloat(recordedPayout.amount).toFixed(2)} to {recordedPayout.vendor_name}</p>
                  <p className="text-sm text-zinc-500">Payout recorded successfully</p>
                </div>
                <div className="flex gap-2 justify-center pt-2">
                  <ScanInvoiceButton
                    vendorName={recordedPayout.vendor_name}
                    vendorId={recordedPayout.vendor_id}
                    amount={recordedPayout.amount}
                    category={selectedVendor?.vendor_type === "expenses" ? "Expense" : "Invoice"}
                    payoutId={recordedPayout.id}
                    onDocCreated={() => {}}
                  />
                  <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vendor-payout-done">Done</Button>
                </div>
              </div>
            ) : (
              /* Normal form */
              <>
            <div>
              <Label>Select Vendor</Label>
              <Select value={selectedVendor?.id || ""} onValueChange={(id) => {
                if (id === "__add_new__") { setShowVendorDialog(true); return; }
                setSelectedVendor(vendors.find(v => v.id === id));
              }}>
                <SelectTrigger className="mt-1" data-testid="vendor-payout-vendor-trigger">
                  <SelectValue placeholder="Choose a vendor..." />
                </SelectTrigger>
                <SelectContent data-testid="vendor-payout-vendor-options">
                  <SelectItem value="__add_new__" className="text-blue-600 font-medium" data-testid="vendor-option-add-new">
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      + Add New Vendor
                    </div>
                  </SelectItem>
                  {vendors.map(vendor => {
                    const isExpense = vendor.vendor_type === "expenses";
                    return (
                      <SelectItem key={vendor.id} value={vendor.id} data-testid={`vendor-option-${vendor.id}`}>
                        <div className="flex items-center gap-2">
                          {isExpense
                            ? <Receipt className="w-4 h-4 text-orange-500" data-testid="expense-vendor-icon" />
                            : <ShoppingCart className="w-4 h-4 text-emerald-600" data-testid="purchase-vendor-icon" />
                          }
                          {vendor.company_name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="payout-amount">Payout Amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input id="payout-amount" type="text" inputMode="decimal" value={amount}
                  onChange={(e) => { if (e.target.value === "" || /^-?\d*\.?\d{0,2}$/.test(e.target.value)) setAmount(e.target.value); }}
                  onBlur={() => { if (amount && !isNaN(parseFloat(amount))) setAmount(parseFloat(amount).toFixed(2)); }}
                  placeholder="0.00" className="pl-7 text-lg font-semibold" data-testid="vendor-payout-amount" />
              </div>
            </div>

            <div>
              <Label htmlFor="payout-notes">Notes (optional)</Label>
              <Textarea id="payout-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this payout..." className="mt-1" rows={2} data-testid="vendor-payout-notes" />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="vendor-payout-cancel">Cancel</Button>
              <Button onClick={handleSubmitPayout} disabled={loading || !selectedVendor || !amount}
                className="flex-1 bg-green-600 hover:bg-green-700" data-testid="vendor-payout-submit">
                <DollarSign className="w-4 h-4 mr-1" /> Record Payout
              </Button>
            </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <VendorDialog open={showVendorDialog} onOpenChange={setShowVendorDialog} onVendorAdded={handleVendorAdded} />
    </>
  );
}
